import { HighlightIcon, Tooltip } from '@dev-spendesk/grapes';

import { type I18nKey } from 'src/core/common/hooks/useTranslation';
import { PayableType } from 'src/core/modules/bookkeep/types';
import type { RequestType } from 'src/core/modules/requests/models/requestType';

type Props = {
  type: RequestType | PayableType;
  tooltipContent?: string;
};

export const TypeToHighlightIcon = ({ type, tooltipContent }: Props) => {
  const icon = getIconForType(type);

  if (tooltipContent) {
    return (
      <Tooltip content={tooltipContent} placement="top" triggerAsChild>
        {icon}
      </Tooltip>
    );
  }

  return icon;
};

const getIconForType = (type: RequestType | PayableType) => {
  switch (type) {
    case 'card_load':
    case 'subscription_increase':
      return <HighlightIcon size={32} name="card-arrow-up" variant="purple" />;
    case PayableType.cardPurchase:
    case 'single_purchase':
      return <HighlightIcon size={32} name="card-lightning" variant="purple" />;
    case 'multi_use_card':
      return <HighlightIcon size={32} name="card-stack" variant="purple" />;
    case 'plastic_card':
      return <HighlightIcon size={32} name="card" variant="purple" />;
    case 'subscription':
      return (
        <HighlightIcon size={32} name="card-rounded-arrows" variant="purple" />
      );
    case PayableType.creditNote:
    case 'credit_note':
      return (
        <HighlightIcon
          size={32}
          name="document-uturn-left-top"
          variant="peach"
        />
      );
    case PayableType.invoicePurchase:
    case 'invoice':
      return <HighlightIcon size={32} name="document" variant="peach" />;
    case 'purchase_order':
      return <HighlightIcon size={32} name="shopping-cart" variant="ocean" />;
    case PayableType.claimedBill:
    case 'expense':
      return <HighlightIcon size={32} name="receipt" variant="lemon" />;
    case PayableType.mileageExpense:
    case 'mileage_allowance':
      return <HighlightIcon size={32} name="car" variant="lemon" />;
    case PayableType.perDiemExpense:
    case 'per_diem_allowance':
      return <HighlightIcon size={32} name="globe-eu-africa" variant="lemon" />;
    case PayableType.cardReversal:
      return (
        <HighlightIcon
          size={32}
          name="receipt-uturn-left-top"
          variant="purple"
        />
      );
    case 'travel_budget':
      return <HighlightIcon size={32} name="plane" variant="pink" />;
    case 'travel_booking':
      // TODO: change the icon when we have a better one for travel booking
      return <HighlightIcon size={32} name="plane" variant="pink" />;
    default:
      return (
        <HighlightIcon size={32} name="receipt-question" variant="carbon" />
      );
  }
};

export const getPayableTypeI18nKey = (
  type: RequestType | PayableType,
): I18nKey => {
  switch (type) {
    case 'card_load':
      return 'requests.types.card_load';
    case 'subscription_increase':
      return 'requests.types.subscription_increase';
    case PayableType.cardPurchase:
    case 'single_purchase':
      return 'payables.panel.types.singleUseCardPurchase';
    case 'multi_use_card':
      return 'payables.panel.types.multiUseCardPurchase';
    case 'subscription':
      return 'payables.panel.types.subscriptionCardPurchase';
    case PayableType.creditNote:
    case 'credit_note':
      return 'payables.panel.types.creditNote';
    case PayableType.invoicePurchase:
    case 'invoice':
      return 'payables.panel.types.invoice';
    case 'purchase_order':
      return 'requests.types.purchase_order';
    case PayableType.claimedBill:
    case 'expense':
      return 'payables.panel.types.expenseClaim';
    case PayableType.mileageExpense:
    case 'mileage_allowance':
      return 'payables.panel.types.mileage';
    case PayableType.perDiemExpense:
    case 'per_diem_allowance':
      return 'payables.panel.types.perDiem';
    case PayableType.cardReversal:
      return 'payables.panel.types.reversal';
    case 'travel_budget':
      return 'payables.panel.types.travelBudget';
    case 'travel_booking':
      return 'requests.types.travel_booking';
    default:
      return 'payables.panel.types.cardPurchase';
  }
};
